import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: unknown, item: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  loading?: boolean;
  emptyMessage?: string;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  onSort,
  loading = false,
  emptyMessage = 'No data available.',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback(
    (key: string) => {
      let direction: 'asc' | 'desc' = 'asc';
      if (sortKey === key && sortDirection === 'asc') {
        direction = 'desc';
      }
      setSortKey(key);
      setSortDirection(direction);
      onSort?.(key, direction);
    },
    [sortKey, sortDirection, onSort],
  );

  const renderSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null;

    if (sortKey !== column.key) {
      return <ChevronsUpDown className="ml-1 inline h-4 w-4 text-gray-400 dark:text-gray-500" />;
    }

    return sortDirection === 'asc' ? (
      <ChevronUp className="ml-1 inline h-4 w-4 text-primary-600 dark:text-primary-400" />
    ) : (
      <ChevronDown className="ml-1 inline h-4 w-4 text-primary-600 dark:text-primary-400" />
    );
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, rowIndex) => (
      <tr key={rowIndex}>
        {columns.map((col) => (
          <td key={col.key} className="px-4 py-3">
            <div className="h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 ${
                  column.sortable ? 'cursor-pointer select-none hover:text-gray-900 dark:hover:text-white' : ''
                }`}
                onClick={column.sortable ? () => handleSort(column.key) : undefined}
              >
                <span className="inline-flex items-center">
                  {column.label}
                  {renderSortIcon(column)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
          {loading ? (
            renderSkeletonRows()
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={column.key}
                    className={`px-4 py-3 text-sm text-gray-700 dark:text-gray-300 ${colIndex === 0 ? '' : 'whitespace-nowrap'}`}
                  >
                    {column.render
                      ? column.render(item[column.key], item)
                      : (item[column.key] as React.ReactNode) ?? '—'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
